import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Stepper, Step, StepLabel, Button, Typography, Box, Paper,
  Table, TableHead, TableRow, TableCell, TableBody,
  Select, MenuItem, FormControl, InputLabel, TextField, Checkbox,
  FormControlLabel, CircularProgress, Chip,
} from "@material-ui/core";
import WarningIcon from "@material-ui/icons/Warning";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import HistoryIcon from "@material-ui/icons/History";
import GetAppIcon from "@material-ui/icons/GetApp";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import BulkImportResult from "./BulkImportResult";
import BulkSendHistory from "./BulkSendHistory";

const META_PROVIDERS = ["meta", "meta_coexistence"];

const useStyles = makeStyles((theme) => ({
  panel: { padding: theme.spacing(2), display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" },
  headerRow: { display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: theme.spacing(1), flexShrink: 0, marginBottom: theme.spacing(1) },
  body: { flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" },
  root: { display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" },
  stepContent: { flex: 1, marginTop: theme.spacing(2), minHeight: 0, overflow: "auto" },
  nav: {
    display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: theme.spacing(2),
    flexShrink: 0, paddingTop: theme.spacing(1.5), borderTop: `1px solid ${theme.palette.divider}`,
  },
  dropZone: {
    border: "2px dashed #bdbdbd", borderRadius: 8, padding: theme.spacing(5),
    textAlign: "center", cursor: "pointer", color: theme.palette.text.secondary,
  },
  warnBox: {
    background: "#FFF4E5", border: "1px solid #FFB74D", borderRadius: 8,
    padding: theme.spacing(2), display: "flex", gap: theme.spacing(1), alignItems: "flex-start",
    marginTop: theme.spacing(2),
  },
  warnBrief: {
    background: "#FFF4E5", border: "1px solid #FFB74D", borderRadius: 6,
    padding: theme.spacing(1), display: "flex", gap: theme.spacing(1), alignItems: "center",
    fontSize: "0.85rem",
  },
  counters: { display: "flex", gap: theme.spacing(2), flexWrap: "wrap", marginBottom: theme.spacing(2) },
  counter: { textAlign: "center", minWidth: 80, padding: theme.spacing(1), border: `1px solid ${theme.palette.divider}`, borderRadius: 8 },
  summaryRow: { display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid #eee" },
  errBox: {
    background: "#FDECEA", border: "1px solid #E57373", borderRadius: 8,
    padding: theme.spacing(1.5), display: "flex", gap: theme.spacing(1), alignItems: "flex-start",
    marginTop: theme.spacing(1.5),
  },
}));

// feat_210 estándar — perfil con templateVarsMode "trimToTemplate": la plantilla
// define cuántas variables se usan. Columnas que necesita una plantilla de N
// variables en el modelo estándar: Telefono y Nombre siempre; DatoK si usa K+1.
const STD_ALWAYS = ["Telefono", "Nombre"];
const neededColumns = (n) => [
  ...STD_ALWAYS,
  ...Array.from({ length: Math.max(0, n - 1) }, (_, i) => `Dato${i + 1}`),
];

// "Falta Dato2 en N filas" a partir de summary.filterBreakdown.
const breakdownLines = (summary) =>
  (summary?.filterBreakdown || []).map((b) =>
    b.op === "notEmpty"
      ? i18n.t("bulkSend.standard.missingInRows", { column: b.column, count: b.count })
      : i18n.t("bulkSend.standard.ruleDiscarded", { column: b.column, count: b.count })
  );

// feat_348 — en una descarga (responseType blob) el error llega como Blob:
// leerlo antes de toastear o el backend roto se ve como un error genérico.
const readBlobError = async (err) => {
  if (err?.response?.data instanceof Blob) {
    try {
      const text = await err.response.data.text();
      try {
        err.response.data = JSON.parse(text);
      } catch {
        err.response.data = { message: text };
      }
    } catch {
      // Blob ilegible: dejamos el error como vino.
    }
  }
  return err;
};

const STEP_KEYS = ["upload", "preview", "template", "options", "confirm"];

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const renderTemplateBody = (bodyText, vars) => {
  let body = bodyText || "";
  (vars || []).forEach((v, i) => {
    body = body.replace(new RegExp(`\\{\\{\\s*${i + 1}\\s*\\}\\}`, "g"), v ?? "");
  });
  return body;
};

const BulkSendPanel = ({ profileId, companyId }) => {
  const classes = useStyles();
  const [showHistory, setShowHistory] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  // upload
  const [fileName, setFileName] = useState("");
  const [fileB64, setFileB64] = useState("");
  // preview
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  // template
  const [connections, setConnections] = useState([]);
  const [whatsappId, setWhatsappId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [loadingTpl, setLoadingTpl] = useState(false);
  // feat_210 — última conexión+plantilla usada por este profile (preselect, editable)
  const [lastUsedWhatsappId, setLastUsedWhatsappId] = useState(null);
  const [lastUsedTemplateId, setLastUsedTemplateId] = useState(null);
  const appliedConnPreselect = useRef(false);
  const appliedTplPreselect = useRef(false);
  // options
  const [throttle, setThrottle] = useState(5);
  const [optIn, setOptIn] = useState(false);
  // feat_210 turnosContext — modo de contexto (solo si el profile trae mapping)
  const [hasTurnosContext, setHasTurnosContext] = useState(false);
  const [contextMode, setContextMode] = useState("normal");
  const appliedContextModePreselect = useRef(false);
  // submit
  const [submitting, setSubmitting] = useState(false);
  const [dedupe, setDedupe] = useState(null); // {previousImport}
  const [resultImportId, setResultImportId] = useState(null);
  // feat_210 estándar — perfil estándar (link al modelo) y modo trimToTemplate
  const [standardKey, setStandardKey] = useState(null);
  const [templateVarsMode, setTemplateVarsMode] = useState(null);
  const isTrim = templateVarsMode === "trimToTemplate";
  // preview re-corrido con la plantilla elegida (sólo trimToTemplate)
  const [tplPreview, setTplPreview] = useState(null);
  const [loadingTplPreview, setLoadingTplPreview] = useState(false);
  const tplPreviewSeq = useRef(0);

  const profileVarCount = useMemo(() => {
    const sample = preview?.sampleRows?.[0];
    return sample?.normalizedData?.templateVars?.length ?? 0;
  }, [preview]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === templateId),
    [templates, templateId]
  );

  // trimToTemplate: validByVarCount {0..K} del preview sin plantilla.
  const validByVarCount = preview?.summary?.validByVarCount || null;
  const profileMaxVars = useMemo(() => {
    if (!validByVarCount) return 0;
    return Object.keys(validByVarCount).map(Number).reduce((m, n) => Math.max(m, n), 0);
  }, [validByVarCount]);
  const anyValidByVarCount = !!validByVarCount &&
    Object.values(validByVarCount).some((v) => Number(v) > 0);
  const tplVarCount = Number(selectedTemplate?.variableCount) || 0;
  const tplExceeds = isTrim && !!selectedTemplate && tplVarCount > profileMaxVars;
  // Lo que se va a enviar: con trimToTemplate, el preview con la plantilla.
  const effectivePreview = isTrim ? tplPreview : preview;

  // ── Upload ──
  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    try {
      const b64 = await fileToBase64(f);
      setFileB64(b64);
      setPreview(null);
      setTplPreview(null);
    } catch (_) {
      toast.error(i18n.t("bulkSend.upload.readError"));
    }
  };

  // ── Preview ──
  const runPreview = useCallback(async () => {
    setLoadingPreview(true);
    try {
      const { data } = await api.post("/bulk-send/preview", { profileId, fileBase64: fileB64 });
      setPreview(data);
      setActiveStep(1);
    } catch (e) {
      toastError(e);
    } finally {
      setLoadingPreview(false);
    }
  }, [profileId, fileB64]);

  // feat_210 — última conexión+plantilla usada por este profile (fetch temprano,
  // hay tiempo de sobra antes de que el operador llegue al Paso 3).
  const fetchLastUsed = useCallback(async () => {
    if (!profileId) return;
    try {
      const { data } = await api.get(`/bulk-send/profiles/${profileId}`);
      setLastUsedWhatsappId(data?.lastUsedWhatsappId || null);
      setLastUsedTemplateId(data?.lastUsedTemplateId || null);
      setStandardKey(data?.standardKey || null);
      setTemplateVarsMode(data?.templateVarsMode || null);
      // feat_210 turnosContext — el switch solo se muestra si el profile trae
      // mapping; preselecciona el último modo usado (una sola vez).
      setHasTurnosContext(!!data?.hasTurnosContextMapping);
      if (
        !appliedContextModePreselect.current &&
        (data?.lastUsedContextMode === "turnosia" || data?.lastUsedContextMode === "normal")
      ) {
        appliedContextModePreselect.current = true;
        setContextMode(data.lastUsedContextMode);
      }
    } catch (_) {
      // preselect es una sugerencia, no crítico — sin toast
    }
  }, [profileId]);

  useEffect(() => { fetchLastUsed(); }, [fetchLastUsed]);

  // ── Connections ──
  useEffect(() => {
    if (activeStep !== 2 || connections.length) return;
    (async () => {
      try {
        const { data } = await api.get("/whatsapp/");
        const metas = (data || []).filter(
          (w) => META_PROVIDERS.includes(w.provider) && w.status === "CONNECTED"
        );
        setConnections(metas);
        if (metas.length === 1) {
          setWhatsappId(metas[0].id);
        } else if (
          !appliedConnPreselect.current &&
          lastUsedWhatsappId &&
          metas.some((w) => Number(w.id) === Number(lastUsedWhatsappId))
        ) {
          appliedConnPreselect.current = true;
          setWhatsappId(lastUsedWhatsappId);
        }
      } catch (e) {
        toastError(e);
      }
    })();
  }, [activeStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Templates on connection change ──
  useEffect(() => {
    if (!whatsappId) { setTemplates([]); setTemplateId(""); return; }
    setLoadingTpl(true);
    (async () => {
      try {
        const { data } = await api.get(`/meta-templates/${whatsappId}`, { params: { status: "APPROVED" } });
        setTemplates(data || []);
        if (
          !appliedTplPreselect.current &&
          lastUsedTemplateId &&
          Number(whatsappId) === Number(lastUsedWhatsappId) &&
          (data || []).some((t) => Number(t.id) === Number(lastUsedTemplateId))
        ) {
          appliedTplPreselect.current = true;
          setTemplateId(lastUsedTemplateId);
        }
      } catch (e) {
        toastError(e);
        setTemplates([]);
      } finally {
        setLoadingTpl(false);
      }
    })();
  }, [whatsappId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── trimToTemplate: preview real con la plantilla elegida ──
  // Mismo archivo (ya en memoria) + templateId → el backend recorta las
  // variables y aplica sólo los filtros que esa plantilla necesita.
  useEffect(() => {
    if (!isTrim) return;
    setTplPreview(null);
    if (!templateId || !fileB64 || !selectedTemplate || tplExceeds) {
      setLoadingTplPreview(false);
      return;
    }
    const seq = ++tplPreviewSeq.current;
    setLoadingTplPreview(true);
    (async () => {
      try {
        const { data } = await api.post("/bulk-send/preview", { profileId, fileBase64: fileB64, templateId });
        if (seq === tplPreviewSeq.current) setTplPreview(data);
      } catch (e) {
        if (seq === tplPreviewSeq.current) toastError(e);
      } finally {
        if (seq === tplPreviewSeq.current) setLoadingTplPreview(false);
      }
    })();
  }, [isTrim, templateId, fileB64, profileId, selectedTemplate, tplExceeds]);

  // ── Descargar modelo de Excel (perfil estándar) ──
  const downloadTemplate = async () => {
    try {
      const lang = (i18n.language || "es").slice(0, 2);
      const response = await api.get("/bulk-send/template-file", {
        params: { lang },
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = i18n.t("bulkSend.standard.downloadFileName");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toastError(await readBlobError(err));
    }
  };

  // ── Submit ──
  // Fix feat_210 Fase C-bis: axios rechaza (throw) cualquier respuesta HTTP
  // >=300 por default. El dedupe 409 con {requiresConfirmation, previousImport}
  // llegaba SIEMPRE al catch, nunca al branch `if (data?.requiresConfirmation)`
  // que vivía en el try — ese branch era código muerto. Ahora se detecta
  // explícitamente en el catch por status+shape antes de caer al toast genérico.
  const submit = async (acknowledge = false) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bulk-send/imports", {
        profileId,
        fileBase64: fileB64,
        whatsappId,
        templateId,
        throttleSeconds: throttle,
        optInConfirmed: true,
        dedupeWarningAcknowledged: acknowledge,
        filename: fileName,
        contextMode: hasTurnosContext ? contextMode : "normal",
      });
      toast.success(i18n.t("bulkSend.confirm.sentOk"));
      setResultImportId(data.import.id);
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.requiresConfirmation) {
        setDedupe(e.response.data.previousImport || {});
        return;
      }
      toastError(e);
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    setActiveStep(0); setFileName(""); setFileB64(""); setPreview(null); setTplPreview(null);
    setWhatsappId(""); setTemplateId(""); setThrottle(5); setOptIn(false);
    setDedupe(null); setResultImportId(null); setConnections([]);
    setContextMode("normal");
    // permite que el próximo envío vuelva a preseleccionar (lastUsed ya se
    // actualizó en el backend con este envío recién hecho)
    appliedConnPreselect.current = false;
    appliedTplPreselect.current = false;
    appliedContextModePreselect.current = false;
    fetchLastUsed();
  };

  // trimToTemplate: la cantidad de variables se adapta a la plantilla → no hay
  // mismatch (el tope lo marca tplExceeds).
  const varMismatch = !isTrim && selectedTemplate && selectedTemplate.variableCount !== profileVarCount;

  const canNext = () => {
    switch (activeStep) {
      case 0: return !!fileB64;
      case 1: return isTrim ? anyValidByVarCount : (preview?.summary?.valid ?? 0) > 0;
      case 2:
        if (!whatsappId || !templateId) return false;
        if (isTrim) {
          return !tplExceeds && !loadingTplPreview && (tplPreview?.summary?.valid ?? 0) > 0;
        }
        return !varMismatch;
      case 3: return optIn;
      default: return true;
    }
  };

  const handleNext = () => {
    if (activeStep === 0) { runPreview(); return; }
    setActiveStep((s) => Math.min(4, s + 1));
  };

  return (
    <div className={classes.panel}>
      <div className={classes.headerRow}>
        {standardKey && (
          <Button size="small" color="primary" startIcon={<GetAppIcon />} onClick={downloadTemplate}>
            {i18n.t("bulkSend.standard.downloadTemplate")}
          </Button>
        )}
        <Button
          size="small" color="primary" startIcon={<HistoryIcon />}
          onClick={() => setShowHistory((s) => !s)}
        >
          {showHistory ? i18n.t("bulkSend.history.backToWizard") : i18n.t("bulkSend.history.viewHistory")}
        </Button>
      </div>

      <div className={classes.body}>
        {showHistory ? (
          <BulkSendHistory companyId={companyId} />
        ) : resultImportId ? (
          <BulkImportResult importId={resultImportId} companyId={companyId} onBack={resetAll} />
        ) : (
          <div className={classes.root}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {STEP_KEYS.map((k) => (
                <Step key={k}><StepLabel>{i18n.t(`bulkSend.steps.${k}`)}</StepLabel></Step>
              ))}
            </Stepper>

            <div className={classes.stepContent}>
              {/* STEP 0 — UPLOAD */}
              {activeStep === 0 && (
                <Box>
                  <input id="bulk-file" type="file" accept=".xlsx" style={{ display: "none" }} onChange={handleFile} />
                  <label htmlFor="bulk-file">
                    <div className={classes.dropZone}>
                      <CloudUploadIcon style={{ fontSize: 48 }} />
                      <Typography>{fileName ? i18n.t("bulkSend.upload.selected", { name: fileName }) : i18n.t("bulkSend.upload.hint")}</Typography>
                      <Button component="span" variant="outlined" style={{ marginTop: 12 }}>{i18n.t("bulkSend.upload.choose")}</Button>
                    </div>
                  </label>
                </Box>
              )}

              {/* STEP 1 — PREVIEW */}
              {activeStep === 1 && preview && isTrim && (() => {
                const missingReq = (preview.summary.columns?.missing || []).filter((c) => STD_ALWAYS.includes(c));
                return (
                  <Box>
                    <div className={classes.counters}>
                      <div className={classes.counter}>
                        <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#1976d2" }}>{preview.summary.total}</div>
                        <Typography variant="caption" color="textSecondary">{i18n.t("bulkSend.preview.total")}</Typography>
                      </div>
                    </div>
                    <Typography variant="subtitle2">{i18n.t("bulkSend.standard.byVarCountTitle")}</Typography>
                    <Box style={{ margin: "4px 0 8px" }}>
                      {Array.from({ length: profileMaxVars }, (_, i) => i + 1).map((n) => (
                        <Typography key={n} variant="body2" style={{ color: Number(validByVarCount?.[n]) > 0 ? undefined : "#757575" }}>
                          {n === 1
                            ? i18n.t("bulkSend.standard.byVarCountOne", { count: validByVarCount?.[n] ?? 0 })
                            : i18n.t("bulkSend.standard.byVarCount", { n, count: validByVarCount?.[n] ?? 0 })}
                        </Typography>
                      ))}
                    </Box>
                    {missingReq.length > 0 && (
                      <div className={classes.errBox}>
                        <WarningIcon style={{ color: "#d32f2f" }} />
                        <Typography variant="body2">{i18n.t("bulkSend.standard.missingRequired", { columns: missingReq.join(", ") })}</Typography>
                      </div>
                    )}
                    {!anyValidByVarCount && (
                      <div className={classes.warnBox}>
                        <WarningIcon style={{ color: "#ed6c02" }} />
                        <Box>
                          <Typography variant="body2">{i18n.t("bulkSend.standard.noValidAny")}</Typography>
                          {breakdownLines(preview.summary).map((l) => <Typography key={l} variant="body2">• {l}</Typography>)}
                        </Box>
                      </div>
                    )}
                    <Typography variant="subtitle2" style={{ marginTop: 12 }}>{i18n.t("bulkSend.preview.sample")}</Typography>
                    {(preview.sampleRows || []).length === 0 ? (
                      <Typography variant="body2" color="textSecondary">{i18n.t("bulkSend.standard.noSample")}</Typography>
                    ) : (
                      <Paper variant="outlined" style={{ maxHeight: 240, overflow: "auto" }}>
                        <Table size="small">
                          <TableHead><TableRow>
                            <TableCell>{i18n.t("bulkSend.preview.colName")}</TableCell>
                            <TableCell>{i18n.t("bulkSend.preview.colPhone")}</TableCell>
                            <TableCell>{i18n.t("bulkSend.preview.colVars")}</TableCell>
                          </TableRow></TableHead>
                          <TableBody>
                            {(preview.sampleRows || []).map((r) => (
                              <TableRow key={r.rowIndex}>
                                <TableCell>{r.normalizedData?.name}</TableCell>
                                <TableCell>{r.normalizedData?.phone}</TableCell>
                                <TableCell>{(r.normalizedData?.templateVars || []).join(" | ")}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Paper>
                    )}
                  </Box>
                );
              })()}

              {activeStep === 1 && preview && !isTrim && (
                <Box>
                  <div className={classes.counters}>
                    {[
                      ["total", preview.summary.total, "#1976d2"],
                      ["valid", preview.summary.valid, "#2e7d32"],
                      ["skipped", (preview.summary.total - preview.summary.valid), "#757575"],
                    ].map(([k, v, c]) => (
                      <div key={k} className={classes.counter}>
                        <div style={{ fontSize: "1.5rem", fontWeight: 700, color: c }}>{v}</div>
                        <Typography variant="caption" color="textSecondary">{i18n.t(`bulkSend.preview.${k}`)}</Typography>
                      </div>
                    ))}
                  </div>
                  <Typography variant="caption" color="textSecondary">
                    {i18n.t("bulkSend.preview.skFilter")}: {preview.summary.skipped.filter} · {i18n.t("bulkSend.preview.skPhone")}: {preview.summary.skipped.invalidPhone} · {i18n.t("bulkSend.preview.skDup")}: {preview.summary.skipped.dupInBatch}
                  </Typography>
                  {preview.summary.valid === 0 && (
                    <div className={classes.warnBox}><WarningIcon style={{ color: "#ed6c02" }} /><Typography variant="body2">{i18n.t("bulkSend.preview.noValid")}</Typography></div>
                  )}
                  <Typography variant="subtitle2" style={{ marginTop: 12 }}>{i18n.t("bulkSend.preview.sample")}</Typography>
                  <Paper variant="outlined" style={{ maxHeight: 240, overflow: "auto" }}>
                    <Table size="small">
                      <TableHead><TableRow>
                        <TableCell>{i18n.t("bulkSend.preview.colName")}</TableCell>
                        <TableCell>{i18n.t("bulkSend.preview.colPhone")}</TableCell>
                        <TableCell>{i18n.t("bulkSend.preview.colVars")}</TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {(preview.sampleRows || []).map((r) => (
                          <TableRow key={r.rowIndex}>
                            <TableCell>{r.normalizedData?.name}</TableCell>
                            <TableCell>{r.normalizedData?.phone}</TableCell>
                            <TableCell>{(r.normalizedData?.templateVars || []).join(" | ")}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </Box>
              )}

              {/* STEP 2 — TEMPLATE + CONNECTION */}
              {activeStep === 2 && (
                <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel>{i18n.t("bulkSend.template.connLabel")}</InputLabel>
                    <Select value={whatsappId} onChange={(e) => setWhatsappId(e.target.value)} label={i18n.t("bulkSend.template.connLabel")}>
                      {connections.map((w) => <MenuItem key={w.id} value={w.id}>{w.name} ({w.number})</MenuItem>)}
                    </Select>
                  </FormControl>
                  {connections.length === 0 && <Typography variant="body2" color="error">{i18n.t("bulkSend.template.noConn")}</Typography>}
                  {!!whatsappId && Number(whatsappId) === Number(lastUsedWhatsappId) && (
                    <Typography variant="caption" color="textSecondary" style={{ marginTop: -12 }}>
                      {i18n.t("bulkSend.template.lastUsedHint")}
                    </Typography>
                  )}

                  {loadingTpl ? <CircularProgress size={24} /> : (
                    <FormControl variant="outlined" fullWidth disabled={!whatsappId}>
                      <InputLabel>{i18n.t("bulkSend.template.tplLabel")}</InputLabel>
                      <Select value={templateId} onChange={(e) => setTemplateId(e.target.value)} label={i18n.t("bulkSend.template.tplLabel")}>
                        {templates.map((t) => <MenuItem key={t.id} value={t.id}>{t.name} ({t.variableCount} vars)</MenuItem>)}
                      </Select>
                    </FormControl>
                  )}
                  {!!templateId && Number(templateId) === Number(lastUsedTemplateId) && (
                    <Typography variant="caption" color="textSecondary" style={{ marginTop: -12 }}>
                      {i18n.t("bulkSend.template.lastUsedHint")}
                    </Typography>
                  )}
                  {whatsappId && !loadingTpl && templates.length === 0 && <Typography variant="body2" color="textSecondary">{i18n.t("bulkSend.template.noTpl")}</Typography>}

                  {selectedTemplate && (
                    <Box>
                      <Typography variant="subtitle2">{i18n.t("bulkSend.template.varsTitle")}</Typography>
                      <Paper variant="outlined" style={{ padding: 12, whiteSpace: "pre-wrap" }}>
                        {renderTemplateBody(selectedTemplate.bodyText, effectivePreview?.sampleRows?.[0]?.normalizedData?.templateVars)}
                      </Paper>
                      {isTrim && tplExceeds && (
                        <div className={classes.errBox}>
                          <WarningIcon style={{ color: "#d32f2f" }} />
                          <Typography variant="body2">{i18n.t("bulkSend.standard.varsExceed", { tpl: tplVarCount, max: profileMaxVars })}</Typography>
                        </div>
                      )}
                      {isTrim && !tplExceeds && loadingTplPreview && (
                        <Box display="flex" alignItems="center" style={{ gap: 8, marginTop: 12 }}>
                          <CircularProgress size={18} />
                          <Typography variant="body2" color="textSecondary">{i18n.t("bulkSend.standard.checking")}</Typography>
                        </Box>
                      )}
                      {isTrim && !tplExceeds && !loadingTplPreview && tplPreview && (() => {
                        const missing = (tplPreview.summary.columns?.missing || []).filter((c) => neededColumns(tplVarCount).includes(c));
                        const valid = tplPreview.summary.valid;
                        return (
                          <>
                            <Typography variant="body2" style={{ marginTop: 12, fontWeight: 600, color: valid > 0 ? "#2e7d32" : "#d32f2f" }}>
                              {valid > 0
                                ? i18n.t("bulkSend.standard.tplValid", { count: valid, total: tplPreview.summary.total })
                                : i18n.t("bulkSend.standard.tplNoValid")}
                            </Typography>
                            {missing.length > 0 && (
                              <div className={classes.errBox}>
                                <WarningIcon style={{ color: "#d32f2f" }} />
                                <Typography variant="body2">{i18n.t("bulkSend.standard.missingForTemplate", { n: tplVarCount, columns: missing.join(", ") })}</Typography>
                              </div>
                            )}
                            {valid === 0 && breakdownLines(tplPreview.summary).length > 0 && (
                              <div className={classes.warnBox}>
                                <WarningIcon style={{ color: "#ed6c02" }} />
                                <Box>{breakdownLines(tplPreview.summary).map((l) => <Typography key={l} variant="body2">• {l}</Typography>)}</Box>
                              </div>
                            )}
                          </>
                        );
                      })()}
                      {varMismatch && (
                        <div className={classes.warnBox}>
                          <WarningIcon style={{ color: "#d32f2f" }} />
                          <Typography variant="body2">{i18n.t("bulkSend.template.varMismatch", { tpl: selectedTemplate.variableCount, prof: profileVarCount })}</Typography>
                        </div>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* STEP 3 — OPTIONS */}
              {activeStep === 3 && (
                <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
                  <TextField
                    type="number" variant="outlined" label={i18n.t("bulkSend.options.throttle")}
                    helperText={i18n.t("bulkSend.options.throttleHelp")}
                    value={throttle}
                    onChange={(e) => { const v = e.target.value; setThrottle(Math.min(60, Math.max(1, Number(v) || 1))); }}
                    inputProps={{ min: 1, max: 60 }} style={{ maxWidth: 220 }}
                  />
                  {hasTurnosContext && (
                    <FormControl variant="outlined" style={{ maxWidth: 320 }}>
                      <InputLabel>{i18n.t("bulkSend.options.contextModeLabel")}</InputLabel>
                      <Select
                        value={contextMode}
                        onChange={(e) => setContextMode(e.target.value)}
                        label={i18n.t("bulkSend.options.contextModeLabel")}
                      >
                        <MenuItem value="normal">{i18n.t("bulkSend.options.contextModeNormal")}</MenuItem>
                        <MenuItem value="turnosia">{i18n.t("bulkSend.options.contextModeTurnosia")}</MenuItem>
                      </Select>
                      <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
                        {i18n.t("bulkSend.options.contextModeHint")}
                      </Typography>
                    </FormControl>
                  )}
                  <div className={classes.warnBox}>
                    <WarningIcon style={{ color: "#ed6c02" }} />
                    <Box>
                      <Typography variant="subtitle2">{i18n.t("bulkSend.options.overwriteTitle")}</Typography>
                      <Typography variant="body2">{i18n.t("bulkSend.options.overwrite")}</Typography>
                    </Box>
                  </div>
                  <FormControlLabel
                    control={<Checkbox checked={optIn} onChange={(e) => setOptIn(e.target.checked)} color="primary" />}
                    label={i18n.t("bulkSend.options.optIn")}
                  />
                </Box>
              )}

              {/* STEP 4 — CONFIRM */}
              {activeStep === 4 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>{i18n.t("bulkSend.confirm.title")}</Typography>
                  <Paper variant="outlined" style={{ padding: 12 }}>
                    <div className={classes.summaryRow}><span>{i18n.t("bulkSend.confirm.file")}</span><b>{fileName}</b></div>
                    <div className={classes.summaryRow}><span>{i18n.t("bulkSend.confirm.validRows")}</span><b>{effectivePreview?.summary?.valid ?? 0}</b></div>
                    <div className={classes.summaryRow}><span>{i18n.t("bulkSend.confirm.conn")}</span><b>{connections.find((w) => w.id === whatsappId)?.name}</b></div>
                    <div className={classes.summaryRow}><span>{i18n.t("bulkSend.confirm.tpl")}</span><b>{selectedTemplate?.name}</b></div>
                    <div className={classes.summaryRow}><span>{i18n.t("bulkSend.confirm.throttle")}</span><b>{throttle}s</b></div>
                  </Paper>
                  <div className={classes.warnBrief}>
                    <WarningIcon style={{ color: "#ed6c02", fontSize: 18 }} />
                    <span>{i18n.t("bulkSend.confirm.warnBrief")}</span>
                  </div>
                  {dedupe && (
                    <div className={classes.warnBox} style={{ flexDirection: "column" }}>
                      <Box display="flex" style={{ gap: 8 }}>
                        <WarningIcon style={{ color: "#d32f2f" }} />
                        <Typography variant="body2">
                          {i18n.t("bulkSend.dedupe.msg", { date: dedupe.createdAt ? new Date(dedupe.createdAt).toLocaleString() : "" })}
                        </Typography>
                      </Box>
                      <Box style={{ marginTop: 8 }}>
                        <Button size="small" color="secondary" variant="contained" disabled={submitting} onClick={() => submit(true)}>
                          {i18n.t("bulkSend.dedupe.confirm")}
                        </Button>
                        <Button size="small" style={{ marginLeft: 8 }} onClick={() => setDedupe(null)}>{i18n.t("bulkSend.dedupe.cancel")}</Button>
                      </Box>
                    </div>
                  )}
                </Box>
              )}
            </div>

            <div className={classes.nav}>
              <Button disabled={activeStep === 0 || submitting} onClick={() => setActiveStep((s) => Math.max(0, s - 1))}>
                {i18n.t("bulkSend.back")}
              </Button>
              {activeStep < 4 ? (
                <Button
                  variant="contained" color="primary" size="large"
                  disabled={!canNext() || loadingPreview} onClick={handleNext}
                >
                  {loadingPreview ? <CircularProgress size={20} color="inherit" /> : i18n.t("bulkSend.next")}
                </Button>
              ) : (
                <Button
                  variant="contained" color="primary" size="large"
                  disabled={submitting || !!dedupe} onClick={() => submit(false)}
                >
                  {submitting ? <CircularProgress size={20} color="inherit" /> : i18n.t("bulkSend.confirm.send")}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkSendPanel;
